import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [statusUrl, setStatusUrl] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    linkedin_url: "",
    portfolio_url: "",
    resume_url: "",
    years_experience: "",
    cover_letter: "",
    consent: false,
  });

  useEffect(() => {
    axios
      .get(`${API}/jobs/${slug}`)
      .then((r) => setJob(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.consent) {
      toast.error("Please acknowledge the hiring privacy consent before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/applications`, { ...form, job_id: slug });
      setSubmitted(true);
      setStatusUrl(data?.status_url || (data?.status_token ? `/status/${data.status_token}` : null));
      toast.success("Application received — we’ll be in touch.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send application. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) return <Navigate to="/careers" replace />;
  if (!job)
    return (
      <div className="tr-container py-40 text-ink/50">Loading role…</div>
    );

  return (
    <div data-testid={`job-detail-${job.id}`} className="bg-bone">
      {/* HERO */}
      <section className="pt-20 md:pt-28 pb-12 border-b border-border">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-8">
            <Link
              to="/careers"
              data-testid="job-back"
              className="inline-flex items-center gap-2 text-sm link-underline mb-10 text-ink/60 hover:text-ink"
            >
              <ArrowLeft className="w-4 h-4" /> All roles
            </Link>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-ink/50 mb-8">
              <span>{job.department}</span>
              <span className="hidden md:inline">·</span>
              <span>{job.location}</span>
              <span className="hidden md:inline">·</span>
              <span>{job.employment_type}</span>
              <span className="hidden md:inline">·</span>
              <span>{job.experience}</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              {job.title}
              <br />
              <span className="italic text-ink/60">{job.level}</span>
            </h1>
            <p className="mt-8 text-xl text-ink/70 leading-relaxed max-w-3xl">
              {job.summary}
            </p>
            {job.focus && job.focus.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2" data-testid="job-focus">
                {job.focus.map((f) => (
                  <span
                    key={f}
                    className="inline-block px-3 py-1.5 border border-ink/20 text-[11px] uppercase tracking-[0.18em] text-ink/70"
                  >
                    {f}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* DETAILS */}
      <section className="py-20 md:py-28">
        <div className="tr-container grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 md:col-span-5 md:sticky md:top-24 md:self-start">
            <div className="space-y-6 border-t border-border pt-6">
              <div>
                <div className="overline text-vermilion mb-2">Team</div>
                <div className="text-ink">{job.department}</div>
              </div>
              <div>
                <div className="overline text-vermilion mb-2">Location</div>
                <div className="text-ink">{job.location}</div>
              </div>
              <div>
                <div className="overline text-vermilion mb-2">Experience</div>
                <div className="text-ink">{job.experience}</div>
              </div>
              {job.tech_stack && job.tech_stack.length > 0 && (
                <div data-testid="job-tech-stack">
                  <div className="overline text-vermilion mb-2">Tech stack</div>
                  <ul className="space-y-1.5 text-sm text-ink/80">
                    {job.tech_stack.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
              <a
                href="#apply"
                data-testid="job-apply-cta"
                className="btn-accent w-full"
              >
                Apply for this role <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            <div>
              <div className="overline text-vermilion mb-4">What you’ll do</div>
              <ul className="divide-y divide-border border-y border-border">
                {job.responsibilities.map((r, i) => (
                  <Reveal as="li" key={i} delay={i} className="py-4 flex gap-5">
                    <span className="font-mono text-xs text-ink/40 pt-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-ink leading-relaxed">{r}</p>
                  </Reveal>
                ))}
              </ul>
            </div>

            <div className="mt-16">
              <div className="overline text-vermilion mb-4">What we’re looking for</div>
              <ul className="divide-y divide-border border-y border-border">
                {job.requirements.map((r, i) => (
                  <Reveal as="li" key={i} delay={i} className="py-4 flex gap-5">
                    <span className="font-mono text-xs text-vermilion pt-1">→</span>
                    <p className="text-ink leading-relaxed">{r}</p>
                  </Reveal>
                ))}
              </ul>
            </div>

            {job.nice_to_have?.length > 0 && (
              <div className="mt-16">
                <div className="overline text-vermilion mb-4">Nice to have</div>
                <ul className="divide-y divide-border border-y border-border">
                  {job.nice_to_have.map((r, i) => (
                    <li key={i} className="py-4 flex gap-5 text-ink/70">
                      <span className="font-mono text-xs text-ink/40 pt-1">+</span>
                      <p className="leading-relaxed">{r}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* APPLY */}
      <section id="apply" className="py-24 md:py-32 bg-ink text-white">
        <div className="tr-container grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 md:col-span-5">
            <div className="overline text-vermilion mb-6">Apply</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Tell us about yourself.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              A short, candid note goes further than a template. We read every
              application ourselves.
            </p>
          </div>

          <div className="col-span-12 md:col-span-7">
            {submitted ? (
              <div
                data-testid="application-success"
                className="border border-white/20 p-10 md:p-12"
              >
                <CheckCircle2 className="w-8 h-8 text-vermilion mb-6" />
                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
                  Application received.
                </h3>
                <p className="text-white/70 max-w-lg mb-8">
                  Thank you for writing in. Our team reviews every application
                  personally — expect to hear back within 7 working days.
                </p>
                {statusUrl && (
                  <div
                    data-testid="status-link-block"
                    className="border-t border-white/20 pt-6 space-y-3"
                  >
                    <div className="overline text-vermilion">
                      Track your application
                    </div>
                    <p className="text-sm text-white/70 max-w-lg">
                      Bookmark this link — it always shows the current stage of
                      your application in real time, without needing an account.
                    </p>
                    <Link
                      to={statusUrl}
                      data-testid="status-link"
                      className="inline-flex items-center gap-2 border border-white px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-ink transition-colors"
                    >
                      Open status page{" "}
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                    <p className="font-mono text-[11px] text-white/40 break-all">
                      {typeof window !== "undefined" ? window.location.origin : ""}
                      {statusUrl}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <form
                data-testid="application-form"
                onSubmit={onSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    label="Full name*"
                    id="full_name"
                    value={form.full_name}
                    onChange={update("full_name")}
                    required
                  />
                  <Field
                    label="Email*"
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                  <Field
                    label="Phone"
                    id="phone"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                  <Field
                    label="Years of experience"
                    id="years_experience"
                    value={form.years_experience}
                    onChange={update("years_experience")}
                    placeholder="e.g. 6"
                  />
                  <Field
                    label="LinkedIn URL"
                    id="linkedin_url"
                    value={form.linkedin_url}
                    onChange={update("linkedin_url")}
                    placeholder="https://linkedin.com/in/…"
                  />
                  <Field
                    label="Portfolio / GitHub URL"
                    id="portfolio_url"
                    value={form.portfolio_url}
                    onChange={update("portfolio_url")}
                    placeholder="https://github.com/…"
                  />
                </div>
                <Field
                  label="Resume URL (Google Drive, Dropbox, etc.)"
                  id="resume_url"
                  value={form.resume_url}
                  onChange={update("resume_url")}
                  placeholder="https://…"
                />
                <div className="space-y-2">
                  <Label
                    htmlFor="cover_letter"
                    className="overline text-white/60"
                  >
                    Why this role? (Cover note)
                  </Label>
                  <Textarea
                    id="cover_letter"
                    rows={6}
                    value={form.cover_letter}
                    onChange={update("cover_letter")}
                    data-testid="field-cover_letter"
                    placeholder="Tell us what you’ve shipped, and what you want to ship next…"
                    className="bg-transparent border border-white/30 text-white placeholder:text-white/40 focus-visible:ring-white/50 rounded-none"
                  />
                </div>

                {/* Hiring privacy consent */}
                <label
                  htmlFor="consent"
                  className="flex items-start gap-3 border border-white/20 p-5 cursor-pointer select-none hover:bg-white/[0.04] transition-colors"
                  data-testid="consent-block"
                >
                  <input
                    id="consent"
                    type="checkbox"
                    required
                    checked={form.consent}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, consent: e.target.checked }))
                    }
                    data-testid="field-consent"
                    className="mt-1 w-4 h-4 accent-vermilion shrink-0"
                  />
                  <span className="text-xs text-white/80 leading-relaxed">
                    I consent to ThinkRight Tech and its hiring partners
                    (including the portfolio company this role sits with)
                    collecting, storing and processing the
                    personal information I share here — including my CV,
                    LinkedIn, portfolio and cover note — for the purpose of
                    evaluating this and future role-fit opportunities, for up to
                    24 months. I have read the{" "}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noreferrer"
                      className="underline underline-offset-2 text-white hover:text-vermilion"
                    >
                      Privacy Policy
                    </Link>{" "}
                    and can withdraw this consent any time by writing to{" "}
                    <a
                      href="mailto:contact@thinkright-tech.com"
                      className="underline underline-offset-2 text-white hover:text-vermilion"
                    >
                      contact@thinkright-tech.com
                    </a>
                    .
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting || !form.consent}
                  data-testid="submit-application"
                  className="inline-flex items-center gap-2 bg-vermilion text-white hover:bg-red-700 px-8 py-4 text-[11px] uppercase tracking-[0.22em] font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "Sending…" : "Send application"}{" "}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, ...props }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="overline text-white/60">
        {label}
      </Label>
      <Input
        id={id}
        data-testid={`field-${id}`}
        {...props}
        className="bg-transparent border-0 border-b border-white/30 text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:border-white rounded-none h-11 px-0 shadow-none"
      />
    </div>
  );
}
